import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';
import * as Constants from 'src/constants';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage
        author='OneShop Team'
        dateTime='2022-10-21'
        tag='Etsy'
        title="Etsy Account Suspended: Here's How to Avoid It"
      >
        <p>
          Etsy has become a go-to place for all sorts of products from furniture to accessories. The marketplace is also
          the perfect spot for artists and crafters looking to sell their creations online. Sellers can set up their own
          stores, list items, and sell to customers all around the world.
        </p>
        <p>
          However, if you're not careful with what you do and violate Etsy's policies, your account may be suspended or
          even worse, banned. Running into issues with your account can be frustrating and upsetting. It can be due to
          the inability to access your store due to technical difficulties, the inability to list new items or more
          severe problems like getting your account temporarily or permanently suspended.
        </p>
        <p>
          To prevent this from happening, you must know what causes an Etsy account suspension and why this happens.
          This article will guide you with everything you need to know to avoid suspensions, how to reinstate your
          account, and some tips on keeping your account safe and your listings active.
        </p>

        <h3>5 Reasons for Account Suspension on Sellers</h3>
        <p>
          Suspension of an Etsy shop can happen to anyone. Whether you are a new or an experienced seller, if you
          infringe Etsy's terms of service agreement, it can result in having your account temporarily or permanently
          suspended.
        </p>
        <p>
          This is a frustrating experience, to say the least, and it can happen for many reasons. There are many things
          you can do to avoid getting suspended, but first, let's explore what causes an Etsy account suspension and why
          this happens so often.
        </p>
        <ul>
          <li>
            <strong>Selling prohibited products</strong>
            <p>
              One main reason your account may be suspended is due to selling restricted or prohibited items. Etsy has a
              zero-tolerance policy for sellers who list banned items. Anything that may present legal risks or harm
              Etsy's community is not allowed to be sold on the platform.
            </p>
            <p>Here are some products that are restricted or prohibited from being sold on Etsy:</p>
            <ul>
              <li>Alcohol, tobacco, medical drugs, drugs, and drug paraphernalia</li>
              <li>Animal and human remains</li>
              <li>Hazardous materials, weapons, and recalled products</li>
              <li>
                Hate items (any items that may promote, support, or glorify violence or hate against any race or group)
              </li>
              <li>Illegal items and items promoting illegal activity</li>
              <li>Internationally regulated items.</li>
              <li>Pornography and mature content</li>
            </ul>
            <p>
              Services are also prohibited from being sold unless they provide a tangible item. This does not apply to
              digital products listed on Etsy.
            </p>
          </li>
          <li>
            <strong>Reselling handmade items</strong>
            <p>
              Etsy is known to have a massive collection of unique vintage finds and handmade items. Most of the things
              you will see on the platform are handmade or designed by the seller. To maintain its uniqueness and
              creativity, Etsy does not allow anyone to buy wholesale items and resell them on the site. If Etsy doubts
              the genuineness of your listings, they may ask you to prove it by requesting progression photos.
            </p>
          </li>
          <li>
            <strong>Pending payment of the overdue balance</strong>
            <p>
              You may have overlooked the pending payment of your outstanding balance, resulting in the suspension of
              your account. This happens when the seller's billing method is refused, and you still have to pay some
              outstanding balance. After paying your overdue balance, you'll be reinstated.
            </p>
          </li>
          <li>
            <strong>Your account is associated with another banned account</strong>
            <p>
              Logging into two shops under the same IP address triggers Etsy's banning algorithm. Although this is
              unusual, it is much safer to avoid logging into your shop at a public place or workplace.
            </p>
          </li>
          <li>
            <strong>Infringing trademarks</strong>
            <p>
              Most of the items listed on Etsy are handmade or designed by the seller. Any unauthorized use of the
              trademark can lead to legal action against the seller.
            </p>
          </li>
        </ul>

        <h3>How to Reinstate Suspended Accounts</h3>
        <p>
          Etsy can suspend your account when you have violated their terms and conditions. The suspension can be
          temporary or permanent, disallowing you from selling or operating on the platform. When this happens, Etsy
          will send you an email informing you about the account suspension.
        </p>
        <p>Reinstating your account varies depending on the reason why your account got suspended.</p>

        <h3>Account Suspension Due to Overdue Balance</h3>
        <p>
          The most common account suspension happens when your billing method is refused, making you unavailable to
          settle your outstanding balance. If you have an overdue statement, you may be subject to penalties such as
          restricted account privileges, shop suspension, and star seller badge removal.
        </p>
        <h4>Why account suspension happens due to overdue balance happens</h4>
        <p>
          Suspension on your account due to unsettled overdue statements happened when you failed to pay your
          outstanding balance on Etsy. You may have been unable to pay your unpaid balance due to the following cases:
        </p>
        <ul>
          <li>
            <strong>If your account is not enrolled in auto-billing</strong>
            <ul>
              <li>Your last month's statement is due on the 1st of this month.</li>
              <li>
                You haven't paid your statement by midnight UTC on the 16th of the month (or 7:00 pm ET on the 15th).
                Your statements will become overdue, and your account may be suspended.
              </li>
            </ul>
          </li>
          <li>
            <strong>If your account is enrolled in auto-billing</strong>
            <ul>
              <li>
                If Etsy cannot process your payment using the card on your file, you will receive an email and on-site
                notifications reminding you to pay your overdue in full within five days.
              </li>
              <li>
                You will still receive incoming orders during the 5-day grace period of nonpayment, but some of your
                account privileges will be restricted.
              </li>
              <li>In case of nonpayment after the 5-day grace period, your account will be suspended.</li>
            </ul>
          </li>
        </ul>
        <h4>How to get your account privilege back:</h4>
        <p>
          After you pay your total current balance, you will automatically get your account back. To pay your overdue
          statements, follow these steps:
        </p>
        <ol>
          <li>Log in to your Etsy account</li>
          <li>
            Select <strong>Shop Manager</strong>
          </li>
          <li>
            Tap <strong>Finances</strong>
          </li>
          <li>
            Select <strong>Payment account</strong>
          </li>
          <li>
            Tap on <strong>Pay now</strong> under your <strong>Amount due</strong>
          </li>
          <li>
            Click <strong>Current Balance</strong>
          </li>
          <li>Choose your payment method</li>
          <li>
            Select <strong>Submit Payment</strong>
          </li>
        </ol>
        <p>
          If you're in the European Economic Area (EEA), which includes EU countries, Iceland, Liechtenstein, and
          Iceland, you may be asked to authenticate your payment before selecting Submit Payment.
        </p>
        <p>
          To authenticate, you have to respond promptly to your card issuer's additional security information request.
          Depending on the card issuer, the additional security information includes a security question, a password, or
          a passcode.
        </p>

        <h3>Account Temporarily Suspended</h3>
        <p>
          Etsy may suspend your account temporarily if you fail to follow its rules and regulations. If this happens,
          you will see a banner on your shop manager displaying the status of your shop, and you may also receive an
          email from Etsy informing you about the status of your account.
        </p>
        <p>
          After receiving the email with details of your account status, you should resolve the issue(s) communicated in
          the email and respond to them directly. Suppose you did not receive an email or received an email from
          noreply@etsy.com and have questions about your temporary account suspension. In that case, you may contact
          Etsy by going to <strong>Etsy Help Center</strong> and selecting <strong>Contact support</strong> at the
          bottom of the page.
        </p>
        <h4>Features you can and cannot use during account suspension</h4>
        <p>
          Some of your account features and abilities are restricted when your shop gets temporarily suspended. If your
          Etsy shop got suspended, here are a few things you can still do:
        </p>
        <ul>
          <li>Access your account by logging in on Etsy.com using a web browser</li>
          <li>You can still read, respond, and create new messages</li>
          <li>You can still complete pending orders and upload a tracking number</li>
          <li>Issue full and partial refunds</li>
          <li>View and resolve open cases on your case history</li>
          <li>View new and completed orders</li>
          <li>View and settle the current balance in your Payment account</li>
        </ul>
        <p>On the other hand, here are the abilities and features your account is restricted from:</p>
        <ul>
          <li>Place new orders on Etsy</li>
          <li>Receive new orders (Buyers will be unable to view your shop on Etsy)</li>
          <li>Access your account using Etsy apps</li>
          <li>Create listings</li>
          <li>Purchase or print Etsy shipping labels</li>
          <li>Cancel existing orders</li>
          <li>Manage and view reviews left by buyers</li>
          <li>Participate in the Etsy Ads campaign</li>
          <li>
            If you have a star seller badge, it will be removed. You may also be ineligible to become a star seller in
            the future.
          </li>
          <li>Etsy may hold your funds in your Etsy payment account</li>
        </ul>

        <h3>Account Permanently Suspended</h3>
        <p>
          Etsy may suspend your account privilege permanently if you infringe on their policies. If you want to continue
          selling on the site, you can file an appeal, and Etsy will take a second look.
        </p>
        <h4>What happens to your account when permanently suspended?</h4>
        <p>
          If Etsy suspends your account permanently, you are restricted from both buying or selling on the platform. You
          may also be unable to sign in to your Etsy account using the apps and the web browser. Furthermore, you cannot
          create a new account to buy or sell on Etsy.
        </p>

        <h3>How to file An Appeal with Etsy</h3>
        <p>
          Etsy members who may be permanently or temporarily suspended will receive an email informing them of the
          account suspension. The email will contain the type of suspension (permanent or temporary) and the reason
          behind the account suspension.
        </p>
        <p>
          If it's a temporary suspension, Etsy will also inform the seller of ways to resolve the issue to get their
          account back up again. If it's a permanent suspension, you will receive an email notifying you that your
          account privileges are permanently suspended. You can file an appeal to Etsy to have your account reviewed for
          reinstatement.
        </p>
        <h4>Filing an Appeal:</h4>
        <p>
          Before you file an appeal to review your account for reinstatement, you must resolve all your outstanding
          account issues. It may include resolving open cases and paying any due balance. To file an appeal, follow
          these steps:
        </p>
        <ol>
          <li>Go to Etsy.com</li>
          <li>
            Click <strong>Etsy Help Center</strong>
          </li>
          <li>
            Click <strong>Your Etsy Account</strong>
          </li>
          <li>
            Select <strong>How to file an appeal for a permanently suspended account</strong> under{' '}
            <strong>Contacting Etsy</strong>
          </li>
          <li>
            Click <strong>File an Appeal</strong>
          </li>
          <li>
            State the reason behind your account suspension and request for appeal. You also have to enter the email
            address associated with your Etsy account.
            <ul>
              <li>
                When stating the reason for your account suspension, also mention the factors that led to permanent
                account suspension.
              </li>
              <li>
                When requesting an appeal, also mention your plans on how to remediate the issues in your account.
              </li>
            </ul>
          </li>
        </ol>
        <p>
          After you file an appeal, an Etsy specialist will review your appeal and account history and respond with a
          final decision via email. Investigation can take up to two weeks before determining if your account will be
          reinstated or not.
        </p>
        <p>
          Etsy specialists may request additional details during the appeal process. If your appeal is unsuccessful,
          your account will remain permanently closed, with no further consideration of reinstatement. You can only file
          an appeal for a permanently suspended account once. So make sure to respond promptly and be transparent with
          any details, as this is your final opportunity to continue your participation in the Etsy marketplace.
        </p>

        <h3>Tips to Avoid Getting Suspended</h3>
        <p>
          To start with, maintaining a good seller reputation and following Etsy's policies will go a long way in
          avoiding getting your account suspended. Below are some helpful tips to ensure your account is safe and you
          can continue selling on Etsy.
        </p>
        <ul>
          <li>
            <strong>Learn what you can and can't sell on Etsy</strong>
            <p>
              Similar to other marketplaces, there are restricted items you cannot list on Etsy. It includes alcohol,
              drugs, illegal items, hazardous materials, and other banned items.
            </p>
            <p>
              You are also prohibited from listing items that promote and glorify hate or violence against any race or
              members of any group. Before you start selling, check the list of prohibited items on Etsy and your
              inventory. You can begin your listing if your item is not included on the list of restricted items.
            </p>
          </li>
          <li>
            <strong>Avoid buying handmade items and reselling them on Etsy</strong>
            <p>
              Etsy is a marketplace focused on selling handmade, vintage, and craft supplies. Many sellers are selling
              items made or designed by them. You cannot buy things wholesale from an Etsy seller and sell them on the
              platform.
            </p>
            <p>
              If Etsy finds out you are reselling handmade items, they can suspend your account. Etsy is very stringent
              in this process to maintain the originality of the platform and may request additional evidence that you
              made or designed the item yourself.
            </p>
          </li>
          <li>
            <strong>Resolve customer complaints professionally</strong>
            <p>
              Customers may report your shop for inappropriate behavior or when you send a different item from the one
              they purchased from your shop. Many accounts are banned or suspended due to fraudulent activities like
              sending something different from what the customer ordered or not sending the products at all after taking
              the money.
            </p>
          </li>
        </ul>

        <h3>Maintain a Good Seller Reputation with OneShop</h3>
        <p>
          Selling on Etsy and other marketplaces is a fruitful venture that can increase your cash flow or lets you save
          extra space in your closet. However, selling online also comes with its pros and cons. If you do not operate
          professionally or follow the rules and policies required by Etsy and other e-commerce platforms, you may
          encounter account issues, including getting your account suspended or banned.
        </p>
        <p>
          Ensuring your seller account complies with all the platform's policies can be tedious and time-consuming,
          especially if you are also selling on other marketplaces. You must consider many things when selling online,
          including the daily listing tasks of relisting stale listings and cross-listing to attract more potential
          customers.
        </p>
        <p>
          If you are a reseller experiencing the same predicament of having never-ending daily tasks of relisting,
          cross-listing, and delisting, then having a reliable automation service is a good option for you. OneShop is
          the trusted automation service of thousands of resellers that lets them do more and saves them more time while
          ensuring their account is safe and their listings active.
        </p>
        <p>
          OneShop is your marketing expert 24/7 that helps you have a seamless listing and selling experience. It allows
          you to work efficiently using a computer and continue your work using your smartphone for a quicker listing
          experience. In addition to its excellent automation features, you can also get peace of mind of not selling a
          single item to multiple marketplaces because of its auto-delisting feature.
        </p>
        <p>
          The auto-delisting feature automatically delists a single item once it is sold, so you don't have to manually
          delist items once you sell them out. This gives you plenty of time to do more critical things like scheduling
          pick-ups or ordering supplies.
        </p>
        <p>
          Try OneShop today to experience how this tool can help your online business succeed.{' '}
          <a href='https://tools.oneshop.com/signup'>Sign-up now</a>, set up your account in less than three minutes,
          and enjoy a <>{Constants.Marketing.TrialDuration}</>-day free trial!
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
